import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../Common/LoadingSpinner';
import inventoryApi from '../../../services/inventoryApi';
import { costCenterApi } from '../../../services/financeApi';
import adminAPI from '../../../services/adminApi';
import { openGRNReport } from '../../../utils/openInventoryReport';
import { canPrintGRN } from '../../../utils/documentPrintGate';
import { useAuth } from '../../../contexts/AuthContext';
import ProcurementDocuments from '../../Common/ProcurementDocuments';

const GRNDetails = ({ grn, warehouses = [], onClose, onEdit, onApprove, onReject, onInspect }) => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isApproving, setIsApproving] = useState(false);
  const [costCenterName, setCostCenterName] = useState('');
  const [locationInfo, setLocationInfo] = useState({});
  const [assetRegister, setAssetRegister] = useState(grn.assetRegister || null);
  const [registering, setRegistering] = useState(false);

  useEffect(() => { setAssetRegister(grn.assetRegister || null); }, [grn]);

  const retryRegister = async (leaveConflictingSerialsPending = false) => {
    setRegistering(true);
    try {
      const res = await inventoryApi.registerGRNAssets(grn._id, { leaveConflictingSerialsPending });
      setAssetRegister(res.data);
      toast.success(res.message || 'Assets registered');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to register the assets');
      if (err.response?.data?.data) setAssetRegister(err.response.data.data);
    } finally {
      setRegistering(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setLocationInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[GRNDetails] could not load location info:', e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!grn?.costCenter) { setCostCenterName(''); return; }
    if (grn.costCenterLabel) { setCostCenterName(grn.costCenterLabel); return; }
    costCenterApi.getCostCenterById(grn.costCenter)
      .then(res => {
        const cc = res.data?.data?.costCenter || res.data?.data || res.data;
        setCostCenterName(cc ? `${cc.code} - ${cc.name}` : grn.costCenter);
      })
      .catch(() => setCostCenterName(grn.costCenter));
  }, [grn?.costCenter, grn?.costCenterLabel]);

  if (!grn) return null;

  const resolveWarehouse = () => {
    if (grn.warehouse?.name) return grn.warehouse;
    const candidates = [grn.warehouse, grn.store, grn.location]
      .filter(v => v && typeof v === 'string');
    for (const id of candidates) {
      const found = warehouses.find(w => String(w._id) === id);
      if (found) return found;
    }
    return null;
  };
  const warehouse = resolveWarehouse();

  const getStatusColor = (status) => {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      received: 'bg-indigo-100 text-indigo-800',
      inspected: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      partially_accepted: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-100 text-red-800',
      returned: 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getInspectionStatusColor = (status) => {
    const statusColors = {
      not_required: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-indigo-100 text-indigo-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const formatDateOnly = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getCompletionPercentage = () => {
    if (!grn.lineItems || grn.lineItems.length === 0) return 0;

    const totalOrdered = grn.lineItems.reduce((sum, item) => sum + (item.orderedQuantity || 0), 0);
    const totalReceived = grn.lineItems.reduce((sum, item) => sum + (item.deliveredQuantity || 0), 0);

    return totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;
  };

  const handlePrint = async () => {
    await openGRNReport(grn, locationInfo);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setLoading(true);
      await inventoryApi.addGRNComment(grn._id, { comment: newComment });
      setNewComment('');
      setShowCommentDialog(false);
      // Refresh GRN data if needed
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (grnId) => {
    if (!onApprove) return;

    try {
      setIsApproving(true);
      const response = await onApprove({ _id: grnId });

      if (response && response.success) {
        toast.success(response.message || 'GRN approved successfully');
        setTimeout(() => {
          onClose();
        }, 1500);
      } 
    } catch (err) {
      console.error('Error approving GRN:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to approve GRN';
      toast.error(`Approval failed: ${errorMessage}`);
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 h-full w-full z-50 flex items-center justify-center p-1">
      <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl border border-gray-100 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Compact Header */}
        <div className="px-4 py-2 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-white/20 rounded-md flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{grn.procurementType === 'SERVICE' ? 'Service Completion' : 'GRN'}: {grn.grnNumber}</h2>
                <p className="text-xs text-indigo-100">{formatDateOnly(grn.receiptDate)} • {grn.supplier?.name || grn.supplier?.supplierName || 'Unknown Supplier'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(grn.status)}`}>
                {grn.status?.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${grn.procurementType === 'FIXED_ASSET' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {grn.procurementType === 'FIXED_ASSET' ? 'FIXED ASSET' : grn.procurementType === 'SERVICE' ? 'SERVICE' : 'INVENTORY'}
              </span>
              {grn.qualityInspection?.required && (
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getInspectionStatusColor(grn.qualityInspection?.status)}`}>
                  QC: {grn.qualityInspection?.status?.replace('_', ' ').toUpperCase() || 'Pending'}
                </span>
              )}
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors ml-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-4 mt-2 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-2">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="mx-4 mt-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded p-2">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-green-800">{success}</p>
              <button
                onClick={() => setSuccess(null)}
                className="ml-auto text-green-600 hover:text-green-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Compact Tab Navigation */}
        <div className="px-4 py-1 border-b border-gray-200">
          <nav className="flex space-x-4">
            {[
              { id: 'overview', name: 'Overview', count: '' },
              { id: 'items', name: 'Items', count: ` (${grn.lineItems?.length || 0})` },
              { id: 'inspection', name: 'QC', count: '' },
              { id: 'financials', name: 'Finance', count: '' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2 py-1.5 text-xs font-medium border-b-2 transition-colors ${activeTab === tab.id
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 border-transparent'
                  }`}
              >
                {tab.name}{tab.count}
              </button>
            ))}
          </nav>
        </div>

        {/* Compact Content */}
        <div className="flex-1 overflow-y-auto p-3">
          {activeTab === 'overview' && (
            <div className="space-y-3">
              {/* Compact Basic Information */}
              <div className="bg-white border border-gray-200 rounded p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <svg className="w-4 h-4 text-indigo-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Basic Info
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block font-medium text-gray-500">GRN Number</label>
                    <p className="font-semibold text-gray-900">{grn.grnNumber}</p>
                  </div>
                  <div>
                    <label className="block font-medium text-gray-500">Purchase Order</label>
                    <p className="text-gray-900">{grn.purchaseOrder?.orderNumber || grn.purchaseOrder?.poNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block font-medium text-gray-500">{grn.procurementType === 'SERVICE' ? 'Completion Date' : 'Receipt Date'}</label>
                    <p className="text-gray-900">{formatDateOnly(grn.receiptDate)}</p>
                  </div>
                  <div>
                    <label className="block font-medium text-gray-500">Cost Center</label>
                    <p className="text-gray-900">{costCenterName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block font-medium text-gray-500">Progress</label>
                    <div className="flex items-center space-x-1">
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-indigo-600 h-1.5 rounded-full transition-all"
                          style={{ width: `${getCompletionPercentage()}%` }}
                        ></div>
                      </div>
                      <span className="font-medium text-indigo-600">{getCompletionPercentage()}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Supplier and Warehouse Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-white border border-gray-200 rounded p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-indigo-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Supplier
                  </h3>
                  <div className="space-y-2 text-xs">
                    <div>
                      <label className="block font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">{grn.supplier?.name || grn.supplier?.supplierName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block font-medium text-gray-500">Contact</label>
                      <p className="text-gray-900">{grn.supplier?.email || grn.supplier?.phone || grn.supplier?.contactInfo?.email || grn.supplier?.contactInfo?.phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-purple-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {grn.procurementType === 'SERVICE' ? 'Service Location' : 'Warehouse'}
                  </h3>
                  {grn.procurementType === 'SERVICE' ? (
                  <div className="space-y-2 text-xs">
                    <div>
                      <label className="block font-medium text-gray-500">Location</label>
                      <p className="text-gray-900">{grn.location?.name || locationInfo.name || 'N/A'}</p>
                    </div>
                    <p className="text-gray-400">No store — services are not received into stock</p>
                  </div>
                  ) : (
                  <div className="space-y-2 text-xs">
                    <div>
                      <label className="block font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">{warehouse?.name || grn.location?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block font-medium text-gray-500">Location</label>
                      <p className="text-gray-900">
                        {[warehouse?.address?.street, warehouse?.address?.city, warehouse?.address?.state]
                          .filter(Boolean).join(', ') ||
                          warehouse?.parentLocation?.name ||
                          warehouse?.code || 'N/A'}
                      </p>
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* Delivery Information */}
              {(grn.deliveryNote?.number || grn.deliveryNote?.date || grn.invoice?.number) && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Document Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{grn.procurementType === 'SERVICE' ? 'Service Report / Job Sheet' : 'Delivery Note'}</label>
                      <p className="text-sm text-gray-900">{grn.deliveryNote?.number || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{grn.procurementType === 'SERVICE' ? 'Service Date' : 'Delivery Date'}</label>
                      <p className="text-sm text-gray-900">{grn.deliveryNote?.date ? formatDateOnly(grn.deliveryNote.date) : 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Invoice Number</label>
                      <p className="text-sm text-gray-900">{grn.invoice?.number || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Invoice Amount</label>
                      <p className="text-sm text-gray-900">{grn.invoice?.amount ? formatCurrency(grn.invoice.amount) : 'N/A'}</p>
                    </div>
                  </div>
                  {(grn.deliveryNote?.remarks || grn.notes) && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-500 mb-1">Notes</label>
                      <p className="text-sm text-gray-900">{grn.deliveryNote?.remarks || grn.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Supporting documents: delivery note, invoice, service report… */}
              <ProcurementDocuments docType="grn" docId={grn._id}
                canEdit={hasPermission('INVENTORY:GRN:UPDATE') || hasPermission('INVENTORY:GRN:CREATE')} />
            </div>
          )}

          {grn.procurementType === 'FIXED_ASSET' && grn.status === 'approved' && (
            <div className={`mb-3 rounded border px-3 py-2 text-xs ${assetRegister?.status === 'registered' ? 'border-purple-200 bg-purple-50 text-purple-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">Asset Register</span>
                {assetRegister?.status !== 'registered' && (
                  <div className="flex gap-2">
                    <button onClick={() => retryRegister(false)} disabled={registering} className="px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
                      {registering ? 'Registering…' : 'Register assets'}
                    </button>
                    {/already registered/i.test(assetRegister?.message || '') && (
                      <button onClick={() => retryRegister(true)} disabled={registering} className="px-2 py-1 rounded border border-amber-600 text-amber-800 hover:bg-amber-100 disabled:opacity-50">
                        Register, leave duplicate serials pending
                      </button>
                    )}
                  </div>
                )}
              </div>
              {assetRegister?.status === 'registered' ? (
                <p className="mt-1">{assetRegister.total} unit(s) registered: <span className="font-mono">{(assetRegister.registerNos || []).join(', ')}</span>{assetRegister.message ? <span className="block text-amber-700">{assetRegister.message}</span> : null}</p>
              ) : (
                <p className="mt-1">Not registered yet{assetRegister?.message ? `: ${assetRegister.message}` : ''}. Each accepted unit needs its own register record.</p>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">Line Items ({grn.lineItems?.length || 0})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Item</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase">Ord</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase">Del</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase">Acc</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase">Rej</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Batch</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Expiry</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Warehouse</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {grn.lineItems?.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div>
                            <div className="font-medium text-gray-900">{item.item?.name || item.item?.itemName || item.description || 'N/A'}</div>
                            {(item.item?.code || item.item?.sku || item.item?.itemCode) && (
                              <div className="text-gray-500">{item.item?.code || item.item?.sku || item.item?.itemCode}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center text-gray-900">{item.orderedQuantity || 0}</td>
                        <td className="px-2 py-2 text-center text-gray-900">{item.deliveredQuantity || 0}</td>
                        <td className="px-2 py-2 text-center text-green-600 font-medium">{item.acceptedQuantity || 0}</td>
                        <td className="px-2 py-2 text-center text-red-600 font-medium">{item.rejectedQuantity || 0}</td>
                        <td className="px-2 py-2 text-center text-gray-900">{formatCurrency(item.unitPrice)}</td>
                        <td className="px-2 py-2 text-center text-gray-900 font-medium">
                          {formatCurrency((item.acceptedQuantity || 0) * (item.unitPrice || 0))}
                        </td>
                        <td className="px-2 py-2 text-gray-900">{item.batchNumber || item.serialNumber || item.lotNumber || 'N/A'}</td>
                        <td className="px-2 py-2 text-gray-900">{item.expiryDate ? formatDateOnly(item.expiryDate) : (item.expiry ? formatDateOnly(item.expiry) : 'N/A')}</td>
                        <td className="px-2 py-2 text-gray-900">
                          {grn.procurementType === 'SERVICE' ? '—' : (item.storageLocation || warehouse?.name || grn.location?.name || 'N/A')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'inspection' && (
            <div className="bg-white border border-gray-200 rounded p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <svg className="w-4 h-4 text-indigo-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Quality Inspection
              </h3>

              {/* Always show item-level QC summary */}
              {grn.lineItems?.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Item Quality Summary</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Item</th>
                          <th className="px-2 py-1.5 text-center font-medium text-gray-500 uppercase">Delivered</th>
                          <th className="px-2 py-1.5 text-center font-medium text-green-600 uppercase">Accepted</th>
                          <th className="px-2 py-1.5 text-center font-medium text-red-500 uppercase">Rejected</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Condition</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Grade</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {grn.lineItems.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 font-medium text-gray-900">
                              {item.item?.name || item.description || 'N/A'}
                              {item.item?.code && <span className="block text-gray-400">{item.item.code}</span>}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-700">{item.deliveredQuantity || 0}</td>
                            <td className="px-2 py-1.5 text-center font-semibold text-green-600">{item.acceptedQuantity || 0}</td>
                            <td className="px-2 py-1.5 text-center font-semibold text-red-500">{item.rejectedQuantity || 0}</td>
                            <td className="px-2 py-1.5 text-gray-700 capitalize">{item.condition || 'good'}</td>
                            <td className="px-2 py-1.5 text-gray-700">{item.qualityGrade || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!grn.qualityInspection?.required ? (
                <div className="text-center py-4 text-gray-400 text-xs border-t border-gray-100 mt-2">
                  Formal quality inspection was not required for this GRN
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 text-xs">
                    <div>
                      <label className="block font-medium text-gray-500">Status</label>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getInspectionStatusColor(grn.qualityInspection.status)}`}>
                        {grn.qualityInspection.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Inspector</label>
                      <p className="text-sm text-gray-900">{grn.qualityInspection?.inspector?.name || grn.inspectedBy?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Inspection Date</label>
                      <p className="text-sm text-gray-900">{grn.qualityInspection?.inspectionDate ? formatDate(grn.qualityInspection.inspectionDate) : (grn.inspectedDate ? formatDate(grn.inspectedDate) : 'N/A')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Overall Result</label>
                      <div className="flex items-center space-x-2">
                        {grn.qualityInspection?.overallResult === 'pass' && (
                          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {grn.qualityInspection?.overallResult === 'fail' && (
                          <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        {grn.qualityInspection?.overallResult === 'conditional' && (
                          <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        )}
                        <span className="text-sm text-gray-900 capitalize">{grn.qualityInspection?.overallResult || 'Pending'}</span>
                      </div>
                    </div>
                  </div>

                  {grn.qualityInspection?.checkpoints?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-3">Inspection Checkpoints</h4>
                      <div className="space-y-2">
                        {grn.qualityInspection?.checkpoints?.map((checkpoint, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-shrink-0">
                              {checkpoint.result === 'pass' && (
                                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {checkpoint.result === 'fail' && (
                                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              {checkpoint.result === 'conditional' && (
                                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{checkpoint.parameter}</p>
                              {checkpoint.remarks && <p className="text-sm text-gray-500">{checkpoint.remarks}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {grn.qualityInspection?.remarks && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Inspection Remarks</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{grn.qualityInspection.remarks}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="space-y-6">
              {/* Financial Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  Financial Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(grn.totals?.totalOrderedValue)}</div>
                    <div className="text-sm text-gray-500 mt-1">Total Ordered Value</div>
                  </div>
                  <div className="text-center p-4 bg-indigo-50 rounded-lg">
                    <div className="text-2xl font-bold text-indigo-600">{formatCurrency(grn.totals?.totalDeliveredValue)}</div>
                    <div className="text-sm text-gray-500 mt-1">Total Delivered Value</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(grn.totals?.totalAcceptedValue)}</div>
                    <div className="text-sm text-gray-500 mt-1">Total Accepted Value</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{formatCurrency(grn.totals?.totalRejectedValue)}</div>
                    <div className="text-sm text-gray-500 mt-1">Total Rejected Value</div>
                  </div>
                </div>

                {/* SSCL / VAT / Grand Total breakdown */}
                {(grn.totals?.ssclAmount > 0 || grn.totals?.vatAmount > 0) && (
                  <div className="mt-4 max-w-xs ml-auto space-y-1.5">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal (Accepted):</span>
                      <span className="font-medium">{formatCurrency(grn.totals?.totalAcceptedValue)}</span>
                    </div>
                    {grn.totals?.ssclAmount > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>SSCL {grn.totals.ssclRate > 0 ? `(${grn.totals.ssclRate}%)` : ''}:</span>
                        <span className="font-medium">{formatCurrency(grn.totals.ssclAmount)}</span>
                      </div>
                    )}
                    {grn.totals?.vatAmount > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>VAT {grn.totals.vatRate > 0 ? `(${grn.totals.vatRate}%)` : ''}:</span>
                        <span className="font-medium">{formatCurrency(grn.totals.vatAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-gray-300 pt-1.5">
                      <span className="text-sm font-bold text-gray-900">Grand Total:</span>
                      <span className="text-sm font-bold text-indigo-700">{formatCurrency(grn.totals?.grandTotal)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* People Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    People Involved
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Received By</label>
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-600">
                            {grn.receivedBy?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <span className="text-sm text-gray-900">{grn.receivedBy?.name || 'N/A'}</span>
                      </div>
                    </div>
                    {grn.approvedBy && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Approved By</label>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-600">
                              {grn.approvedBy.name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm text-gray-900">{grn.approvedBy.name}</span>
                            <div className="text-xs text-gray-500">{formatDate(grn.approvedDate)}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Timestamps
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
                      <p className="text-sm text-gray-900">{formatDate(grn.createdAt)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                      <p className="text-sm text-gray-900">{formatDate(grn.updatedAt)}</p>
                    </div>
                    {grn.approvedDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Approved</label>
                        <p className="text-sm text-gray-900">{formatDate(grn.approvedDate)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments */}
              {grn.comments?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Comments
                  </h4>
                  <div className="space-y-3">
                    {grn.comments.map((comment, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">{comment.user?.name || 'Anonymous'}</span>
                          <span className="text-xs text-gray-500">{formatDate(comment.timestamp)}</span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Comment Dialog */}
        {showCommentDialog && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Comment</h3>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows="4"
                  placeholder="Enter your comment..."
                ></textarea>
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowCommentDialog(false);
                      setNewComment('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer — actions at the bottom of the window */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowCommentDialog(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>Add Comment</span>
          </button>
                {canPrintGRN(grn.status) && (
                  <button
                    onClick={handlePrint}
                    title="Print PDF"
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    <span>Print PDF</span>
                  </button>
                )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
                {grn.status === 'draft' && onEdit && (
                  <button
                    onClick={() => onEdit(grn)}
                    className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors flex items-center space-x-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit</span>
                  </button>
                )}
                {grn.qualityInspection?.required && ['received', 'inspected'].includes(grn.status) && onInspect && (
                  <button
                    onClick={() => onInspect(grn)}
                    className="px-3 py-2 text-sm font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700 transition-colors flex items-center space-x-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <span>Inspect</span>
                  </button>
                )}
                {['inspected', 'accepted', 'partially_accepted'].includes(grn.status) && !grn.approvedBy && onReject && (
                  <button
                    onClick={() => onReject(grn)}
                    className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors flex items-center space-x-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Reject</span>
                  </button>
                )}
                {['inspected', 'accepted', 'pending', 'partially_accepted'].includes(grn.status) && !grn.approvedBy && onApprove && (
                  <button
                    onClick={() => handleApprove(grn._id)}
                    disabled={isApproving}
                    className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                  >
                    {isApproving ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                          <path fill="currentColor" className="opacity-75" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Approving...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Approve</span>
                      </>
                    )}
                  </button>
                )}
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GRNDetails;